/**
 * TREATX-NOTIFICATION-ADMIN-OPS-11B
 * ----------------------------------
 * EmailTemplateOperations
 *
 * READ-ONLY operational layer over the canonical `email_templates`
 * collection. Surfaces per-template operational metadata:
 *   - last modified
 *   - is_default badge
 *   - latest notifications_log row for that event (status + when)
 *
 * The CANONICAL EDITOR remains `/treatx-admin/email-templates`. This
 * page never writes templates; the row click links back to the
 * editor.
 */
import React, { useEffect, useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, Loader2, RefreshCw, ExternalLink,
  CheckCircle2, AlertTriangle, Clock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';

const STATUS_VARIANT = (s) => {
  const v = (s || '').toLowerCase();
  if (v.includes('sent')) return 'bg-emerald-100 text-emerald-700';
  if (v.includes('fail')) return 'bg-rose-100 text-rose-700';
  if (v.includes('block')) return 'bg-amber-100 text-amber-700';
  if (v.includes('queue')) return 'bg-sky-100 text-sky-700';
  return 'bg-slate-100 text-slate-700';
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return formatDateTimeLocale(iso); } catch { return iso; }
};

const EmailTemplateOperations = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/notifications/template-status');
      setItems(r.data?.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="email-template-ops">
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/treatx-admin/notification-ops')}
              className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
              data-testid="tmpl-ops-back-btn"
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Email template operations
              </h1>
              <p className="text-xs text-slate-500">
                Read-only operational view of <code className="font-mono">email_templates</code>. Editor lives at <a className="underline" href="#/treatx-admin/email-templates">/treatx-admin/email-templates</a> (canonical).
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="tmpl-ops-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="bg-white border rounded-2xl overflow-hidden" data-testid="tmpl-ops-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Event key</th>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Origin</th>
                  <th className="px-4 py-2 text-left">Last modified</th>
                  <th className="px-4 py-2 text-left">Last delivery</th>
                  <th className="px-4 py-2 text-right">Editor</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400" data-testid="tmpl-ops-loading">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400" data-testid="tmpl-ops-empty">
                    No templates registered. The canonical store is empty — the editor at <code className="font-mono">/treatx-admin/email-templates</code> will seed defaults on first use.
                  </td></tr>
                )}
                {!loading && items.map((t, idx) => {
                  const last = t.last_delivery || null;
                  return (
                    <tr key={t.event_key || idx} className="border-t hover:bg-slate-50" data-testid={`tmpl-ops-row-${t.event_key || idx}`}>
                      <td className="px-4 py-2 font-mono text-xs">{t.event_key || '—'}</td>
                      <td className="px-4 py-2 text-xs">{t.name || '—'}</td>
                      <td className="px-4 py-2 text-xs">
                        {t.is_default ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] bg-slate-100 text-slate-600">DEFAULT</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-100 text-emerald-700">EDITED</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-600 flex items-center gap-1">
                        <Clock className="h-3 w-3 text-slate-400" /> {fmt(t.last_modified)}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {last ? (
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_VARIANT(last.status)}`}>
                              {last.status || 'unknown'}
                            </span>
                            <span className="text-slate-500">{fmt(last.created_at)}</span>
                            {String(last.status).toLowerCase().includes('sent') && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                            {String(last.status).toLowerCase().includes('fail') && <AlertTriangle className="h-3 w-3 text-rose-500" />}
                          </div>
                        ) : (
                          <span className="text-slate-400">never dispatched</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/treatx-admin/email-templates')}
                          data-testid={`tmpl-ops-open-editor-${t.event_key || idx}`}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" /> Open editor
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-4" data-testid="tmpl-ops-canonicality-note">
          This page reads from the canonical <code className="font-mono">email_templates</code> collection and the canonical <code className="font-mono">notifications_log</code>. No second template store exists.
        </p>
      </div>
    </div>
  );
};

export default EmailTemplateOperations;
